import React, { useState, useEffect, useRef } from "react";
import ReactTable from "react-table";
import "react-table/react-table.css";
import { withRouter } from "react-router";
import SpringApi from "api/SpringApi";
import Loader from "components/common/Loader";
import Toaster from "components/common/Toaster";
import Button from "components/button/Button";

const API = "/api/v1/integrations";

const EMPTY_FORM = { connectionId: "", externalTerminalId: "", externalTerminalName: "", tillId: "" };

const PosMappingsTable = () => {
  const [mappings, setMappings] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loader, setLoader] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formLoading, setFormLoading] = useState(false);
  const [terminals, setTerminals] = useState([]);
  const toaster = useRef();

  useEffect(() => { fetchMappings(); fetchConnections(); }, []);

  const fetchMappings = () => {
    setLoader(true);
    SpringApi.get(`${API}/mappings`)
      .then(res => { setMappings(res.data.data || []); setLoader(false); })
      .catch(err => { if (toaster.current) toaster.current.error(err.message); setLoader(false); });
  };

  const fetchConnections = () => {
    SpringApi.get(`${API}/connections`)
      .then(res => setConnections(res.data.data || []))
      .catch(() => {});
  };

  const handleConnectionChange = (connId) => {
    setFormData({ ...formData, connectionId: connId, externalTerminalId: "", externalTerminalName: "" });
    if (connId) {
      SpringApi.get(`${API}/connections/${connId}/terminals`)
        .then(res => setTerminals(res.data.data || []))
        .catch(() => setTerminals([]));
    } else {
      setTerminals([]);
    }
  };

  const handleTerminalSelect = (terminalId) => {
    const t = terminals.find(x => x.terminalId === terminalId);
    setFormData({ ...formData, externalTerminalId: terminalId, externalTerminalName: t ? t.name : "" });
  };

  const handleSubmit = e => {
    e.preventDefault();
    setFormLoading(true);
    SpringApi.post(`${API}/mappings`, {
      connectionId: parseInt(formData.connectionId, 10),
      externalTerminalId: formData.externalTerminalId,
      externalTerminalName: formData.externalTerminalName,
      tillId: parseInt(formData.tillId, 10)
    })
      .then(() => {
        if (toaster.current) toaster.current.success("Mapping created!");
        setFormData(EMPTY_FORM); setShowForm(false); setTerminals([]); fetchMappings();
      })
      .catch(err => { if (toaster.current) toaster.current.error(err.message); })
      .finally(() => setFormLoading(false));
  };

  const handleDelete = id => {
    if (!window.confirm("Delete this mapping?")) return;
    SpringApi.delete(`${API}/mappings/${id}`)
      .then(() => { if (toaster.current) toaster.current.success("Mapping deleted"); fetchMappings(); })
      .catch(err => { if (toaster.current) toaster.current.error(err.message); });
  };

  const columns = [
    { Header: "ID", accessor: "id", width: 60, className: "text-center", headerClassName: "react-table-header-class", filterable: false },
    { Header: "Provider", accessor: "provider", className: "text-center", headerClassName: "react-table-header-class", filterable: false },
    { Header: "Ext. Store", accessor: "externalStoreId", className: "text-center", headerClassName: "react-table-header-class", filterable: false },
    { Header: "Ext. Terminal", accessor: "externalTerminalId", className: "text-center", headerClassName: "react-table-header-class", filterable: false },
    { Header: "Terminal Name", accessor: "externalTerminalName", className: "text-center", headerClassName: "react-table-header-class", filterable: false },
    { Header: "Till ID", accessor: "tillId", width: 80, className: "text-center", headerClassName: "react-table-header-class", filterable: false },
    {
      Header: "Status", accessor: "active", width: 90, className: "text-center", headerClassName: "react-table-header-class", filterable: false,
      Cell: row => (
        <span style={{ padding: "4px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600,
          backgroundColor: row.value ? "#d4edda" : "#f8d7da", color: row.value ? "#155724" : "#721c24" }}>
          {row.value ? "Active" : "Inactive"}
        </span>
      )
    },
    {
      Header: "Action", accessor: "id", id: "action", width: 100, className: "text-center", headerClassName: "react-table-header-class", filterable: false,
      Cell: row => (
        <Button className="c-btn c-danger" onClick={() => handleDelete(row.value)}>
          <div className="fs-14 medium-text"><i className="fas fa-trash mr-6" /> Delete</div>
        </Button>
      )
    }
  ];

  return (
    <div className="plr-15">
      <div className="roe-card-style mtb-15">
        <div className="roe-card-header module-header">
          <div className="flex-1 fs-16 demi-bold-text"><span className="hash"># </span> Terminal Mappings</div>
          <Button className="c-btn ma-5" onClick={() => setShowForm(!showForm)} style={{ backgroundColor: "#0f172a", color: "#fff" }}>
            <i className={`fas fa-${showForm ? "minus" : "plus"} mr-6`} /> {showForm ? "Cancel" : "Add Mapping"}
          </Button>
        </div>

        {showForm && (
          <div className="roe-card-body" style={{ borderBottom: "1px solid #f0f0f0", paddingBottom: 20 }}>
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-3" style={{ marginBottom: 12 }}>
                  <label className="fs-14 medium-text">POS Connection *</label>
                  <select value={formData.connectionId} onChange={e => handleConnectionChange(e.target.value)}
                    required className="form-control react-form-input">
                    <option value="">Select connection...</option>
                    {connections.map(c => <option key={c.id} value={c.id}>{c.providerDisplayName} — Store {c.storesId}</option>)}
                  </select>
                </div>
                <div className="col-md-3" style={{ marginBottom: 12 }}>
                  <label className="fs-14 medium-text">POS Terminal *</label>
                  {terminals.length > 0 ? (
                    <select value={formData.externalTerminalId} onChange={e => handleTerminalSelect(e.target.value)}
                      required className="form-control react-form-input">
                      <option value="">Select terminal...</option>
                      {terminals.map(t => <option key={t.terminalId} value={t.terminalId}>{t.name} ({t.terminalId})</option>)}
                    </select>
                  ) : (
                    <input type="text" value={formData.externalTerminalId}
                      onChange={e => setFormData({ ...formData, externalTerminalId: e.target.value })}
                      required className="form-control react-form-input" placeholder="Terminal ID" />
                  )}
                </div>
                <div className="col-md-3" style={{ marginBottom: 12 }}>
                  <label className="fs-14 medium-text">GreenTill Till ID *</label>
                  <input type="number" value={formData.tillId}
                    onChange={e => setFormData({ ...formData, tillId: e.target.value })}
                    required className="form-control react-form-input" placeholder="Till ID" />
                </div>
              </div>
              <Button type="submit" className="c-btn" disabled={formLoading}
                style={{ backgroundColor: "#0f172a", color: "#fff", marginTop: 8 }}>
                {formLoading ? <i className="fas fa-ellipsis-h" /> : <><i className="fas fa-save mr-6" /> Save Mapping</>}
              </Button>
            </form>
          </div>
        )}

        <div className="roe-card-body">
          <ReactTable style={{ border: "none", boxShadow: "none" }} data={mappings} columns={columns}
            defaultPageSize={10} minRows={2} className="-striped -highlight custom-react-table-theme-class"
            loading={loader} LoadingComponent={Loader} />
        </div>
        <Toaster ref={toaster} />
      </div>
    </div>
  );
};

export default withRouter(PosMappingsTable);

